import React, { useState, useEffect } from 'react';
import './ChatLogic.css';
import AnimatedCharacter from '../AnimatedCharacter';
import axios from 'axios';
import { FaRobot } from 'react-icons/fa';
import ScrollToBottom from '../ScrollToBottom';
import Login from '../Login';
import ForgotPassword from '../forgotPassword.js';
import ForumPage from './ForumPage.js';
import Register from '../Register';
import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import TJGLogo from '../assets/logo.png';
import PopOutTabs from '../PopOutTabs';
import { useLocation } from 'react-router-dom';

// -----------------------------------------------------------
// Original "preloadedQuestions" and messages from your snippet
// -----------------------------------------------------------
const preloadedQuestions = [
  'What services does the Joachim Group CPAs offer?',
  'How can the Joachim Group help with tax planning?',
  'What are the benefits of hiring the Joachim Group CPAs?',
  'Can the Joachim Group assist with financial audits?',
  'How does the Joachim Group support small businesses?',
];

const ChatLogic = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [emotion, setEmotion] = useState('idle');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [botConversationStarted, setBotConversationStarted] = useState(false);
  const [sessionId, setSessionId] = useState('');

  // For warning messages (like unsubscribed user attempts)
  const [warning, setWarning] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // -------------------------
  // 1) Check Session on Mount
  // -------------------------
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}/check/session`, {
          withCredentials: true,
        });

        if (response.data.isAuthenticated && response.data.user) {
          setIsAuthenticated(true);
          setHasSubscribed(response.data.user.hasSubscribed);
          // Important: set sessionId if it exists
          if (response.data.user.sessionId) {
            setSessionId(response.data.user.sessionId);
            console.log('[checkSession] sessionId =>', response.data.user.sessionId);
          }
        } else {
          setIsAuthenticated(false);
          setHasSubscribed(false);
          setSessionId('');
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setIsAuthenticated(false);
        setHasSubscribed(false);
        setSessionId('');
      } finally {
        setAuthChecked(true);
      }
    };

    checkSession();
  }, []);

  // ------------------------------------------------------
  // 2) Load initial "preloaded" user messages (one-time)
  // ------------------------------------------------------
  useEffect(() => {
    const initialMessages = preloadedQuestions.map((question) => ({
      text: question,
      from: 'user',
      preloaded: true,
    }));
    setMessages(initialMessages);
  }, []);

  // ----------------------------------------------------------------------------
  // 3) Once we know the user's auth status, show welcome message if subscribed
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (authChecked && isAuthenticated && hasSubscribed) {
      setMessages((prevMessages) => {
        // Show welcome message only once
        const welcomeMessage = 'Welcome! How can I assist you today?';
        if (!prevMessages.some((msg) => msg.text === welcomeMessage)) {
          return [
            ...prevMessages,
            { text: welcomeMessage, from: 'bot', hasIcon: true },
          ];
        }
        return prevMessages;
      });
    }
  }, [authChecked, isAuthenticated, hasSubscribed]);

  // ----------------------------------------------
  // Additional checkSession if isAuthenticated changes
  // ----------------------------------------------
  useEffect(() => {
    const recheckSession = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}/check/session`, {
          withCredentials: true,
        });

        if (response.data.isAuthenticated && response.data.user) {
          setIsAuthenticated(true);
          setHasSubscribed(response.data.user.hasSubscribed || false);

          if (response.data.user.sessionId) {
            setSessionId(response.data.user.sessionId);
            console.log('[recheckSession] sessionId =>', response.data.user.sessionId);
          }
        } else {
          setIsAuthenticated(false);
          setHasSubscribed(false);
          setSessionId('');
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setIsAuthenticated(false);
        setHasSubscribed(false);
        setSessionId('');
      }
    };

    recheckSession();
  }, [isAuthenticated]);

  // ----------------------------------------------------------
  // 4) Bot's initial or secondary responses if authenticated
  // ----------------------------------------------------------
  const initiateBotResponse = (message = 'How can I help you?') => {
    if (isAuthenticated && !botConversationStarted) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: message, from: 'bot', hasIcon: true },
      ]);
      setIsSpeaking(true);
      setEmotion('happy');
      setBotConversationStarted(true);

      setTimeout(() => {
        setIsSpeaking(false);
      }, 2000);
    }
  };

  const secondaryBotResponse = (
    message = "That's okay! How else can I assist you today?"
  ) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: message, from: 'bot', hasIcon: true },
    ]);
    setIsSpeaking(true);
    setEmotion('happy');

    setTimeout(() => {
      setIsSpeaking(false);
    }, 2000);
  };

  // ---------------------------------------
  // 5) Main handleSendMessage logic
  // ---------------------------------------
  const handleSendMessage = async (message) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: message, from: 'user', sessionId },
    ]);
    setIsSpeaking(true);
    setEmotion('thinking');

    try {
      // Send to your backend, including sessionId
      const response = await axios.post(`${process.env.REACT_APP_API}/`, {
        text: message,
        from: 'user',
        sessionId,
      });

      const { data } = response;

      // If there's a warning, show it
      if (data.warning) {
        setWarning(data.warning);
        setShowWarning(true);
        // Hide warning after 5s
        setTimeout(() => {
          setShowWarning(false);
          setWarning(null);
        }, 5000);
      }

      // Bot response
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: data.response,
          from: 'bot',
          hasIcon: true,
          sessionId,
        },
      ]);

      setIsSpeaking(false);
      setEmotion('happy');
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSpeaking(false);
      setEmotion('sad');

      // Optionally display an error message
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: 'Sorry, something went wrong. Please try again.',
          from: 'bot',
          hasIcon: true,
        },
      ]);
    }
  };

  const handleInputSendMessage = () => {
    if (input.trim() !== '') {
      handleSendMessage(input);
      setInput('');
    }
  };

  const handlePreloadedQuestionClick = (question) => {
    setMessages((prevMessages) => prevMessages.filter((msg) => msg.text !== question));
    handleSendMessage(question);
  };

  // ------------------------------------------------
  // 6) Logout logic
  // ------------------------------------------------
  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API}/logout`, {}, { withCredentials: true });
      dispatch({ type: 'LOGOUT' });

      // Reset states
      setIsAuthenticated(false);
      setHasSubscribed(false);
      setSessionId('');
      setBotConversationStarted(false);

      // Clear messages to preloaded
      const initialMsgs = preloadedQuestions.map((question) => ({
        text: question,
        from: 'user',
        preloaded: true,
      }));
      setMessages(initialMsgs);

      // Navigate to home
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setHasSubscribed(true);
    navigate('/');
  };

  const location = useLocation();
  const hideChatUI = location.pathname === '/login' || location.pathname === '/register';

  return (
    <div className="chatbot">
      <div className="chat-header">
        <img src={TJGLogo} alt="TJG Logo" className="chat-logo" />
      </div>
      <nav>
        <ul>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li>
            <Link to="/Forum">Forum</Link>
          </li>
          {!isAuthenticated ? (
            <>
              <li>
                <Link to="/login">Login</Link>
              </li>
              <li>
                <Link to="/register">Register</Link>
              </li>
              <li style={{ marginTop: '0.5rem' }}>
                <Link
                  to="/password/help"
                  style={{ 
                    fontSize: '0.8em', 
                    color: 'grey', 
                    textShadow: '1px 1px 1px #000' 
                  }}
                >
                  Forgot Password
                </Link>
              </li>
            </>
          ) : (
            <li>
              <button onClick={handleLogout}>Logout</button>
            </li>
          )}
        </ul>
      </nav>

      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/password/help" element={<ForgotPassword />} />
        <Route path="/register" element={<Register />} />
      </Routes>

      {!hideChatUI && (
        <>
          <AnimatedCharacter isSpeaking={isSpeaking} emotion={emotion} />
          <ScrollToBottom className="messages">
            <div className="messages-container">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.from}`}>
                  {msg.from === 'bot' ? (
                    <div className="message-content">
                      <FaRobot className="robot-icon" />
                      <span className="message-text">{msg.text}</span>
                    </div>
                  ) : msg.preloaded ? (
                    <button
                      className="preloaded-question"
                      onClick={() => handlePreloadedQuestionClick(msg.text)}
                    >
                      {msg.text}
                    </button>
                  ) : (
                    <span className="message-text">{msg.text}</span>
                  )}
                </div>
              ))}
            </div>
          </ScrollToBottom>

          <PopOutTabs initiateBotResponse={initiateBotResponse} />

          <div className="input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleInputSendMessage()}
              placeholder="Type your message..."
            />
            <button onClick={handleInputSendMessage}>Send</button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatLogic;